import * as React from "react";
import { useLocation, useNavigate } from "react-router";
import {
  Button,
  makeStyles,
  shorthands,
  Toast,
  Toaster,
  ToastTitle,
  useId,
  useToastController,
} from "@fluentui/react-components";
import { ArrowLeft20Regular } from "@fluentui/react-icons";
import { KFZRiskForm } from "../components/RisikenTabelleDataGrid";
import { apiUrl } from "../utils/api";

/**
 * Legacy KFZ form route at /risks/new/kfz-old — kept reachable so we can
 * compare the new step form against the original implementation.
 *
 * The form component itself (`KFZRiskForm` exported from
 * RisikenTabelleDataGrid.tsx) is unchanged — we just give it its own
 * page wrapper. Save logic mirrors the original NewRiskPage save handler.
 */

const useStyles = makeStyles({
  root: {
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "16px",
    paddingRight: "16px",
    "@media (min-width: 768px)": {
      paddingTop: "24px",
      paddingBottom: "24px",
      paddingLeft: "24px",
      paddingRight: "24px",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    marginBottom: "16px",
  },
  legacyBadge: {
    display: "inline-flex",
    alignItems: "center",
    paddingTop: "3px",
    paddingBottom: "3px",
    paddingLeft: "8px",
    paddingRight: "8px",
    backgroundColor: "rgba(247,168,35,0.18)",
    color: "#7a4c00",
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    ...shorthands.borderRadius("999px"),
  },
});

const RisikoKfzLegacyPage = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const toasterId = useId("kfz-legacy-toaster");
  const { dispatchToast } = useToastController(toasterId);

  const saveKfzRiskObject = React.useCallback(
    async (data: Record<string, any>) => {
      const classLabel =
        typeof data.vehicleClass === "string" ? data.vehicleClass.trim() : "";
      const plate =
        typeof data.licensePlate === "string" ? data.licensePlate.trim() : "";
      const ort =
        plate.length > 0 ? `${classLabel || "KFZ"}: ${plate}` : classLabel || "KFZ";
      const kontaktId = Number(location.state?.kontaktId);
      const vertragId = Number(location.state?.vertragId);

      const characteristics = Object.entries(data)
        .filter(
          ([, value]) =>
            value !== undefined && value !== null && String(value).trim() !== ""
        )
        .map(([nameBezeichnung, value]) => ({
          nameBezeichnung,
          riskCharacteristicValue: String(value),
        }));

      const res = await fetch(apiUrl("risk-objects"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contractID:
            Number.isFinite(vertragId) && vertragId > 0 ? vertragId : null,
          contactID:
            Number.isFinite(kontaktId) && kontaktId > 0 ? kontaktId : null,
          riskType: "KFZ",
          name: ort,
          characteristics,
        }),
      });

      if (!res.ok) {
        const message = await res.text();
        throw new Error(message || "KFZ speichern fehlgeschlagen");
      }
    },
    [location.state]
  );

  return (
    <div className={styles.root}>
      <Toaster toasterId={toasterId} position="top-end" />
      <div className={styles.header}>
        <Button
          appearance="subtle"
          icon={<ArrowLeft20Regular />}
          onClick={() => navigate("/risks/new")}
        >
          Zurück
        </Button>
        <h2 style={{ margin: 0 }}>KFZ erfassen — Alte Ansicht</h2>
        <span className={styles.legacyBadge}>Legacy</span>
      </div>
      <KFZRiskForm
        onCancel={() => navigate("/risks/new")}
        onSave={async (data) => {
          try {
            await saveKfzRiskObject(data);
            dispatchToast(
              <Toast>
                <ToastTitle>KFZ gespeichert (Legacy)</ToastTitle>
              </Toast>,
              { intent: "success" }
            );
            window.setTimeout(() => navigate("/risks"), 600);
          } catch (e) {
            dispatchToast(
              <Toast>
                <ToastTitle>KFZ konnte nicht gespeichert werden</ToastTitle>
              </Toast>,
              { intent: "error" }
            );
          }
        }}
      />
    </div>
  );
};

export default RisikoKfzLegacyPage;
